"use client";

import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";

import { Modal } from "./Modal";

type ConfirmationVariant = "default" | "danger";

type ConfirmationOptions = {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmationVariant;
};

type ConfirmationContextValue = {
  confirm: (options: ConfirmationOptions) => Promise<boolean>;
};

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null);

export function ConfirmationProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<ConfirmationOptions | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const close = useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setPending(null);
  }, []);

  const confirm = useCallback((options: ConfirmationOptions) => {
    setPending(options);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const value = useMemo<ConfirmationContextValue>(
    () => ({
      confirm,
    }),
    [confirm],
  );

  return (
    <ConfirmationContext.Provider value={value}>
      {children}
      <Modal
        isOpen={Boolean(pending)}
        onClose={() => close(false)}
        title={pending?.title ?? "Confirm action"}
        size="sm"
      >
        <div className="space-y-5">
          <p className="text-sm leading-6 text-[var(--muted-foreground)]">{pending?.description}</p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => close(false)}
              className="rounded-xl border border-[var(--border)] px-4 py-2.5 text-sm font-medium text-[var(--foreground)]"
            >
              {pending?.cancelLabel ?? "Cancel"}
            </button>
            <button
              type="button"
              onClick={() => close(true)}
              className={`rounded-xl px-4 py-2.5 text-sm font-medium ${
                pending?.variant === "danger"
                  ? "bg-rose-600 text-white"
                  : "bg-[var(--primary)] text-[var(--primary-foreground)]"
              }`}
            >
              {pending?.confirmLabel ?? "Confirm"}
            </button>
          </div>
        </div>
      </Modal>
    </ConfirmationContext.Provider>
  );
}

export function useConfirmation() {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error("useConfirmation must be used within ConfirmationProvider");
  }

  return context;
}
